import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  AUTH_TOKEN_KEY,
  AUTH_SESSION_EXPIRED_EVENT,
  clearStoredAuthTokens,
  ensureAccessToken,
  getStoredAccessToken,
  isAccessTokenExpired,
  isSessionDead,
  refreshAccessToken,
  setStoredRefreshToken,
} from '@/lib/authRefresh';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  organizationSlug: string;
  organizationName: string;
  role: string;
  avatarUrl?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  permissions: string[];
  roles: string[];
  token: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  refreshUser: () => Promise<void>;
  roles: string[];
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = AUTH_TOKEN_KEY;

const BOOT_RETRY_ATTEMPTS = 12;
const BOOT_RETRY_BASE_MS = 400;

/** One boot sequence app-wide (survives React Strict Mode remounts). */
let bootSessionInFlight: Promise<void> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableStatus(status: number): boolean {
  return status >= 502 && status <= 504;
}

async function fetchMeWithToken(authToken: string): Promise<Response> {
  return fetch('/api/auth/me', {
    headers: { Authorization: `Bearer ${authToken}` },
    credentials: 'include',
  });
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    permissions: [],
    roles: [],
    token: null,
  });

  const fetchCurrentUser = useCallback(async (token?: string): Promise<void> => {
    const runBoot = async (): Promise<void> => {
      for (let attempt = 0; attempt < BOOT_RETRY_ATTEMPTS; attempt++) {
        try {
          let authToken = token ?? getStoredAccessToken();

          const ensured = await ensureAccessToken();
          if (ensured.ok) {
            authToken = ensured.token;
          } else if (ensured.reason === 'network' && attempt < BOOT_RETRY_ATTEMPTS - 1) {
            await sleep(BOOT_RETRY_BASE_MS * (attempt + 1));
            continue;
          } else if (!ensured.ok) {
            const fallbackToken = getStoredAccessToken();
            if (fallbackToken && !isAccessTokenExpired(fallbackToken)) {
              authToken = fallbackToken;
            } else if (isSessionDead()) {
              setState({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                permissions: [],
                roles: [],
                token: null,
              });
              if (ensured.reason === 'unauthorized') {
                clearStoredAuthTokens();
              }
              return;
            } else {
              authToken = fallbackToken;
            }
          }

          if (!authToken) {
            setState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              permissions: [],
              roles: [],
              token: null,
            });
            return;
          }

          let res = await fetchMeWithToken(authToken);
          if (res.status === 401) {
            const refreshed = await refreshAccessToken();
            if (refreshed.ok) {
              authToken = refreshed.token;
              res = await fetchMeWithToken(authToken);
            } else if (!refreshed.ok) {
              const fallbackToken = getStoredAccessToken();
              if (fallbackToken && !isAccessTokenExpired(fallbackToken)) {
                authToken = fallbackToken;
                res = await fetchMeWithToken(authToken);
              } else if (isSessionDead()) {
                setState({
                  isAuthenticated: false,
                  isLoading: false,
                  user: null,
                  permissions: [],
                  roles: [],
                  token: null,
                });
                if (refreshed.reason === 'unauthorized') {
                  clearStoredAuthTokens();
                }
                return;
              } else if (refreshed.reason === 'network' && attempt < BOOT_RETRY_ATTEMPTS - 1) {
                await sleep(BOOT_RETRY_BASE_MS * (attempt + 1));
                continue;
              } else {
                setState({
                  isAuthenticated: false,
                  isLoading: false,
                  user: null,
                  permissions: [],
                  roles: [],
                  token: null,
                });
                return;
              }
            }
          }

          if (isRetryableStatus(res.status) && attempt < BOOT_RETRY_ATTEMPTS - 1) {
            await sleep(BOOT_RETRY_BASE_MS * (attempt + 1));
            continue;
          }

          if (!res.ok) {
            setState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              permissions: [],
              roles: [],
              token: res.status === 401 ? null : authToken,
            });
            if (res.status === 401) {
              clearStoredAuthTokens();
            }
            return;
          }

          const data = await res.json();
          const userData = data.user ?? data;
          const permissions: string[] = userData.permissions ?? data.permissions ?? [];
          const roles: string[] = userData.roles ?? data.roles ?? [];

          const user: User = {
            id: userData.id,
            email: userData.email,
            firstName: userData.firstName ?? '',
            lastName: userData.lastName ?? '',
            organizationId: userData.organizationId ?? '',
            organizationSlug: userData.organizationSlug ?? '',
            organizationName: userData.organizationName ?? '',
            role: roles[0] ?? userData.role ?? '',
            avatarUrl: userData.avatarUrl,
          };

          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            permissions,
            roles,
            token: authToken,
          });
          return;
        } catch {
          if (attempt < BOOT_RETRY_ATTEMPTS - 1) {
            await sleep(BOOT_RETRY_BASE_MS * (attempt + 1));
            continue;
          }
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            permissions: [],
            roles: [],
            token: getStoredAccessToken(),
          });
          return;
        }
      }
    };

    if (!bootSessionInFlight) {
      bootSessionInFlight = runBoot().finally(() => {
        bootSessionInFlight = null;
      });
    }
    await bootSessionInFlight;
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    fetchCurrentUser(storedToken ?? undefined);
  }, [fetchCurrentUser]);

  useEffect(() => {
    const onSessionExpired = (): void => {
      clearStoredAuthTokens();
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        permissions: [],
        roles: [],
        token: null,
      });
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  /** Refresh access token before the 15m JWT expires. */
  useEffect(() => {
    if (!state.isAuthenticated) return undefined;

    const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

    const refresh = async (): Promise<void> => {
      const ensured = await ensureAccessToken();
      if (ensured.ok) {
        setState((prev) => ({ ...prev, token: ensured.token }));
      }
    };

    const id = window.setInterval(refresh, REFRESH_INTERVAL_MS);

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state.isAuthenticated]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    // Do not toggle global isLoading — AuthOrLogin unmounts <Login /> while
    // isLoading is true, which clears local error UI after a failed attempt.
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    if (!res.ok) {
      const errorData = (await res.json().catch(() => null)) as {
        error?: unknown;
        message?: unknown;
      } | null;
      const fromError = typeof errorData?.error === 'string' ? errorData.error : null;
      const fromMessage = typeof errorData?.message === 'string' ? errorData.message : null;
      throw new Error(fromError || fromMessage || 'Invalid email or password');
    }

    const data = await res.json();
    const token = data.accessToken ?? data.token;

    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    }
    if (typeof data.refreshToken === 'string' && data.refreshToken.length > 0) {
      setStoredRefreshToken(data.refreshToken);
    }

    if (data.user) {
      const loginRoles: string[] = data.user.roles ?? [];
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName ?? '',
          lastName: data.user.lastName ?? '',
          organizationId: data.user.organizationId ?? '',
          organizationSlug: data.user.organizationSlug ?? '',
          organizationName: data.user.organizationName ?? '',
          role: (loginRoles[0] ?? data.user.role) ?? '',
          avatarUrl: data.user.avatarUrl,
        },
        permissions: data.user.permissions ?? [],
        roles: loginRoles,
        token: token ?? null,
      });
    } else {
      await fetchCurrentUser(token);
    }
  }, [fetchCurrentUser]);

  const logout = useCallback((): void => {
    fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }).catch(() => {});
    clearStoredAuthTokens();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      permissions: [],
      roles: [],
      token: null,
    });
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    return state.permissions.includes(permission);
  }, [state.permissions]);

  const refreshUser = useCallback(async (): Promise<void> => {
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  const value: AuthContextValue = {
    ...state,
    roles: state.roles,
    login,
    logout,
    hasPermission,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function usePermission(permission: string): boolean {
  const { permissions } = useAuth();
  return permissions.includes(permission);
}

export function usePermissions(requiredPermissions: string[]): boolean {
  const { permissions } = useAuth();
  return requiredPermissions.every(p => permissions.includes(p));
}
